import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DocumentReference } from '@angular/fire/compat/firestore';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ApiResponse } from 'src/app/models/api-response.model';
import { environment } from 'src/environments/environment';
import { FileUploadResponse, UploadedPatientFile } from '../models/file-upload.model';
import { PatientFiles } from '../models/patient.model';
import { FirestoreService } from './firestore.service';
import { SnackService } from './snack.service';

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  constructor(
    private http: HttpClient,
    private fs: FirestoreService,
    private snackService: SnackService
  ) {}

  /** Uploads one file to the patient's Files tab; the server builds the S3 key. */
  uploadPatientFile(patientId: string, file: File): Observable<UploadedPatientFile> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post<ApiResponse<UploadedPatientFile>>(`${environment.welbyEndpoint}/api/v1/core/patients/${patientId}/files`, formData)
      .pipe(map((res) => res.data));
  }

  /** Asks the API for a short-lived link to a patient file. */
  getPatientFileUrl(patientId: string, fileId: string): Observable<string> {
    return this.http
      .get<ApiResponse<{ url: string }>>(`${environment.welbyEndpoint}/api/v1/core/patients/${patientId}/files/${fileId}/url`)
      .pipe(map((res) => res.data.url));
  }

  /** Opens a patient file in a new tab; call it synchronously from the click, or the popup blocker eats the tab. */
  openPatientFile(patientId: string, fileId: string): void {
    const tab = window.open('', '_blank');
    if (tab) {
      tab.opener = null;
    }
    this.getPatientFileUrl(patientId, fileId)
      .pipe(take(1))
      .subscribe({
        next: (url) => {
          if (tab) {
            tab.location.href = url;
          } else {
            window.open(url, '_blank', 'noopener');
          }
        },
        error: (err) => {
          tab?.close();
          this.snackService.genericSnackBar('Could not open the file', ['error-snackbar'], 5000);
          console.error(`Error in FilesService.openPatientFile(): ${err?.message}`);
        },
      });
  }

  /** Deletes the file from S3 and its patient_files record, server-side. */
  deletePatientFileById(patientId: string, fileId: string): Observable<void> {
    return this.http
      .delete<ApiResponse<void>>(`${environment.welbyEndpoint}/api/v1/core/patients/${patientId}/files/${fileId}`)
      .pipe(map(() => undefined));
  }

  /**
   * Uploads files to the aws S3 via MS.
   *
   * The endpoint answers HTTP 201 even when the upload failed, reporting the failure through
   * `status: 'error'` in the body. That is normalised into a failed observable here so callers have
   * a single error path to handle: reading `uploadedFiles[0].url` off such a response used to throw
   * a TypeError inside the `next` callback, where nothing was watching for it.
   *
   * @param files
   * @param path
   * @returns
   */
  uploadFile(files: File[], path: string, overrideName?: string): Observable<FileUploadResponse> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('path', path);
    if (overrideName) {
      formData.append('overrideName', overrideName);
    }
    const url = `${environment.welbyEndpoint}/api/v1/files/upload`;
    return this.http.post<FileUploadResponse>(`${url}`, formData).pipe(
      map((response) => {
        if (response?.status === 'error' || !response?.uploadedFiles?.[0]?.url) {
          throw new Error(response?.message || 'Upload failed');
        }
        return response;
      })
    );
  }

  /**
   * Removes a file from aws S3 via MS.
   *
   * @param url - The URL of the file to be removed.
   * @returns An Observable that emits the server's response.
   */
  removeFile(url: string): Observable<any> {
    const body = { filePath: url };
    const urlEndpoint = `${environment.welbyEndpoint}/api/v1/files/delete`;
    return this.http.post(urlEndpoint, body);
  }

  addPatientFile(fileData: any): Promise<DocumentReference> {
    return this.fs.col('patient_files').add(fileData);
  }

  getPatientFiles(patientId: string): Observable<PatientFiles[]> {
    return this.fs
      .colWithIds$<PatientFiles>('patient_files', (ref) =>
        ref.where('user_id', '==', patientId).orderBy('createdAt', 'desc')
      )
      .pipe(
        map((files) =>
          files.map((file) => ({
            ...file,
            createdAt:
              file.createdAt instanceof Date
                ? file.createdAt
                : (file.createdAt as firebase.firestore.Timestamp).toDate(),
          }))
        )
      );
  }

  updatePatientFile(fileId: string, newData: PatientFiles): Promise<void> {
    return this.fs.col('patient_files').doc(fileId).update(newData);
  }

  deletePatientFile(fileId: string): Promise<void> {
    return this.fs.col('patient_files').doc(fileId).delete();
  }

  getReturnLabelsByUserId(userId: string): Observable<PatientFiles[]> {
    return this.fs
      .colWithIds$<PatientFiles>('patient_files', (ref) =>
        ref.where('user_id', '==', userId).where('type', '==', 'return-label')
      )
      .pipe(
        map((files) =>
          files
            .map((file) => ({
              ...file,
              createdAt: file.createdAt instanceof Date ? file.createdAt : file.createdAt?.toDate?.(),
              updatedAt: file.updatedAt instanceof Date ? file.updatedAt : file.updatedAt?.toDate?.(),
              return_label_expiration_date:
                file.return_label_expiration_date instanceof Date
                  ? file.return_label_expiration_date
                  : file.return_label_expiration_date?.toDate?.(),
            }))
            .filter((f) => f.return_label_expiration_date && new Date(f.return_label_expiration_date) > new Date())
            .sort((a, b) => +b.createdAt - +a.createdAt)
        )
      );
  }
}
